import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AdminLayout } from '../../components/Layout/AdminLayout';
import { PageHeader, StatCard } from '../../components/common';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Typography,
  CircularProgress,
  Chip,
  LinearProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
  Paper,
  Skeleton,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import {
  Business,
  People,
  AttachMoney,
  Receipt,
  TrendingUp,
  Assessment,
  ShowChart,
  FilterList,
  Refresh,
} from '@mui/icons-material';
import { DataGrid, GridColDef, GridToolbar } from '@mui/x-data-grid';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  Legend,
} from 'recharts';
import { useSuperAdminDashboard, useRecentTransactions, useRevenueChart, useCountyPerformance, useAuth } from '../../hooks';
import { colors } from '../../theme/theme';

// Enhanced currency formatter with better handling
const formatCurrency = (amount: number, options?: { showDecimals?: boolean; compact?: boolean }) => {
  if (!amount && amount !== 0) return 'KES 0';

  const { showDecimals = false, compact = true } = options || {};

  if (compact) {
    if (amount >= 1_000_000_000) {
      const value = amount / 1_000_000_000;
      return `KES ${value % 1 === 0 ? value.toFixed(0) : value.toFixed(1)}B`;
    }
    if (amount >= 1_000_000) {
      const value = amount / 1_000_000;
      return `KES ${value % 1 === 0 ? value.toFixed(0) : value.toFixed(1)}M`;
    }
    if (amount >= 10_000) {
      const value = amount / 1_000;
      return `KES ${value % 1 === 0 ? value.toFixed(0) : value.toFixed(1)}K`;
    }
  }

  return `KES ${amount.toLocaleString('en-KE', {
    minimumFractionDigits: showDecimals ? 2 : 0,
    maximumFractionDigits: showDecimals ? 2 : 0
  })}`;
};

// Tooltip formatter for charts
const formatChartTooltip = (value: any, name?: string | number) => {
  const nameStr = String(name);
  if (nameStr === 'revenue' || nameStr === 'amount') {
    return formatCurrency(Number(value), { showDecimals: false, compact: false });
  }
  if (typeof value === 'number') {
    return value.toLocaleString();
  }
  return value;
};

export const SuperAdminDashboardPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [dateFilter, setDateFilter] = useState('all');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [monthlyFilter, setMonthlyFilter] = useState('last_12_months');
  const [chartStartDate, setChartStartDate] = useState('');
  const [chartEndDate, setChartEndDate] = useState('');
  const [transactionLimit, setTransactionLimit] = useState(10);

  const filterParams = {
    date_filter: dateFilter,
    start_date: dateFilter === 'custom' && startDate ? startDate : undefined,
    end_date: dateFilter === 'custom' && endDate ? endDate : undefined,
  };

  const monthlyParams = {
    monthly_filter: monthlyFilter,
    start_date: monthlyFilter === 'custom' && chartStartDate ? chartStartDate : undefined,
    end_date: monthlyFilter === 'custom' && chartEndDate ? chartEndDate : undefined,
  };

  const { stats, isLoading: statsLoading } = useSuperAdminDashboard(filterParams);
  const { transactions, isLoading: transactionsLoading } = useRecentTransactions({ limit: transactionLimit });
  const { chartData, isLoading: chartLoading } = useRevenueChart(monthlyParams);
  const { performance, isLoading: performanceLoading } = useCountyPerformance(filterParams);

  const handleResetFilters = () => {
    setDateFilter('all');
    setStartDate('');
    setEndDate('');
  };

  const statCards = [
    {
      title: 'Total Counties',
      value: stats?.total_counties || 0,
      icon: <Business sx={{ fontSize: 32 }} />,
      color: colors.secondary.main,
      colorDark: colors.secondary.dark,
      change: '+12%',
      changePositive: true,
    },
    {
      title: 'System Users',
      value: stats?.total_users?.toLocaleString() || 0,
      icon: <People sx={{ fontSize: 32 }} />,
      color: colors.success.main,
      colorDark: colors.success.dark,
      change: '+23%',
      changePositive: true,
    },
    {
      title: 'Total Revenue',
      value: formatCurrency(stats?.total_revenue || 0),
      icon: <AttachMoney sx={{ fontSize: 32 }} />,
      color: colors.warning.main,
      colorDark: colors.warning.dark,
      change: '+18%',
      changePositive: true,
    },
    {
      title: 'All Transactions',
      value: stats?.total_transactions?.toLocaleString() || 0,
      icon: <Receipt sx={{ fontSize: 32 }} />,
      color: colors.info.main,
      colorDark: colors.info.dark,
      change: '+8%',
      changePositive: true,
    },
  ];

  const getStatusColor = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'completed':
      case 'success':
        return 'success';
      case 'pending':
        return 'warning';
      case 'failed':
      case 'error':
        return 'error';
      default:
        return 'default';
    }
  };

  // Skeleton loading component for stat cards
  const StatCardSkeleton = () => (
    <Card
      elevation={0}
      sx={{
        borderRadius: 3,
        border: `1px solid ${colors.grey[200]}`,
        height: '100%',
        minHeight: '180px',
      }}
    >
      <CardContent sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
          <Box sx={{ flex: 1 }}>
            <Skeleton width="60%" height={20} sx={{ mb: 2 }} />
            <Skeleton width="80%" height={48} />
          </Box>
          <Skeleton variant="rectangular" width={56} height={56} sx={{ borderRadius: 2 }} />
        </Box>
        <Skeleton width="40%" height={20} />
      </CardContent>
    </Card>
  );

  const columns: GridColDef[] = [
    {
      field: 'reference',
      headerName: 'Reference',
      width: 200,
      renderCell: (params) => (
        <Typography variant="body2" sx={{ fontWeight: 600, fontFamily: 'monospace' }}>
          {params.value || params.row.transaction_reference || 'N/A'}
        </Typography>
      ),
    },
    {
      field: 'county',
      headerName: 'County',
      width: 180,
      valueGetter: (value, row) => row.county?.name || row.touch_point?.county?.name || 'N/A',
    },
    {
      field: 'customer',
      headerName: 'Customer',
      width: 220,
      valueGetter: (value, row) => {
        const customer = row.customer || row.user;
        return customer ? `${customer.first_name || ''} ${customer.last_name || ''}`.trim() || 'N/A' : 'N/A';
      },
    },
    {
      field: 'amount',
      headerName: 'Amount',
      width: 150,
      renderCell: (params) => (
        <Typography variant="body2" sx={{ fontWeight: 600, color: colors.success.main }}>
          KES {params.value?.toLocaleString() || 0}
        </Typography>
      ),
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 130,
      renderCell: (params) => (
        <Chip
          label={params.value}
          color={getStatusColor(params.value)}
          size="small"
          sx={{ textTransform: 'capitalize' }}
        />
      ),
    },
    {
      field: 'created_at',
      headerName: 'Date',
      width: 120,
      valueGetter: (value) => (value ? new Date(value).toLocaleDateString() : 'N/A'),
    },
  ];

  return (
    <AdminLayout>
      <Box>
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 3 }}>
            <Typography
              variant="body1"
              sx={{
                color: colors.grey[600],
                fontSize: '0.9375rem',
                fontWeight: 500
              }}
            >
              Welcome back, {user?.name?.split(' ')[0] || 'Admin'} 👋
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
              <FormControl size="small" sx={{ minWidth: 200 }}>
                <InputLabel>Period</InputLabel>
                <Select
                  value={dateFilter}
                  label="Period"
                  onChange={(e) => setDateFilter(e.target.value)}
                >
                  <MenuItem value="all">All Time</MenuItem>
                  <MenuItem value="today">Today</MenuItem>
                  <MenuItem value="yesterday">Yesterday</MenuItem>
                  <MenuItem value="this_week">This Week</MenuItem>
                  <MenuItem value="last_week">Last Week</MenuItem>
                  <MenuItem value="this_month">This Month</MenuItem>
                  <MenuItem value="last_month">Last Month</MenuItem>
                  <MenuItem value="this_quarter">This Quarter</MenuItem>
                  <MenuItem value="this_year">This Year</MenuItem>
                  <MenuItem value="last_30_days">Last 30 Days</MenuItem>
                  <MenuItem value="last_90_days">Last 90 Days</MenuItem>
                  <MenuItem value="custom">Custom Range</MenuItem>
                </Select>
              </FormControl>
              {dateFilter === 'custom' && (
                <>
                  <TextField
                    size="small"
                    label="Start Date"
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    sx={{ width: 160 }}
                  />
                  <TextField
                    size="small"
                    label="End Date"
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    sx={{ width: 160 }}
                  />
                </>
              )}
              <Button
                variant="outlined"
                size="small"
                startIcon={<Refresh />}
                onClick={handleResetFilters}
                sx={{
                  borderColor: colors.grey[300],
                  color: colors.grey[700],
                  '&:hover': {
                    borderColor: colors.primary.main,
                    backgroundColor: `${colors.primary.main}08`,
                  },
                }}
              >
                Reset
              </Button>
            </Box>
          </Box>
        </Box>

        {/* Stats Cards */}
        <Grid container spacing={4} mb={5}>
          {statsLoading ? (
            <>
              {[1, 2, 3, 4].map((i) => (
                <Grid key={i} size={{ xs: 12, sm: 6, md: 3 }}>
                  <Skeleton variant="rectangular" height={180} sx={{ borderRadius: 3 }} />
                </Grid>
              ))}
            </>
          ) : (
            statCards.map((stat, index) => (
              <Grid key={index} size={{ xs: 12, sm: 6, md: 3 }}>
                <StatCard
                  title={stat.title}
                  value={stat.value}
                  icon={stat.icon}
                  color={stat.color}
                  delay={index * 100}
                />
              </Grid>
            ))
          )}
        </Grid>

        {/* Charts Section */}
        <Grid container spacing={4} mb={5}>
          {/* Revenue Chart */}
          <Grid size={{ xs: 12, lg: 8 }}>
            <Card
              elevation={0}
              sx={{
                borderRadius: 3,
                border: `1px solid ${colors.grey[200]}`,
                boxShadow: '0 4px 16px rgba(0, 0, 0, 0.08)',
                height: '100%',
                transition: 'all 0.3s ease',
                '&:hover': {
                  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
                  transform: 'translateY(-2px)',
                },
              }}
            >
              <CardHeader
                avatar={
                  <Box
                    sx={{
                      bgcolor: `${colors.primary.main}15`,
                      borderRadius: 2,
                      p: 1,
                      display: 'flex'
                    }}
                  >
                    <ShowChart sx={{ color: colors.primary.main }} />
                  </Box>
                }
                title="System Revenue Trend"
                subheader="Revenue across all counties over time"
                titleTypographyProps={{ variant: 'h6', fontWeight: 700 }}
                subheaderTypographyProps={{ variant: 'body2', color: 'text.secondary' }}
                action={
                  <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
                    <FormControl size="small" sx={{ minWidth: 180 }}>
                      <InputLabel>Chart Period</InputLabel>
                      <Select
                        value={monthlyFilter}
                        label="Chart Period"
                        onChange={(e) => setMonthlyFilter(e.target.value)}
                      >
                        <MenuItem value="last_6_months">Last 6 Months</MenuItem>
                        <MenuItem value="last_12_months">Last 12 Months</MenuItem>
                        <MenuItem value="this_year">This Year</MenuItem>
                        <MenuItem value="last_year">Last Year</MenuItem>
                        <MenuItem value="custom">Custom Range</MenuItem>
                      </Select>
                    </FormControl>
                    {monthlyFilter === 'custom' && (
                      <>
                        <TextField
                          size="small"
                          label="Start"
                          type="date"
                          value={chartStartDate}
                          onChange={(e) => setChartStartDate(e.target.value)}
                          InputLabelProps={{ shrink: true }}
                          sx={{ width: 140 }}
                        />
                        <TextField
                          size="small"
                          label="End"
                          type="date"
                          value={chartEndDate}
                          onChange={(e) => setChartEndDate(e.target.value)}
                          InputLabelProps={{ shrink: true }}
                          sx={{ width: 140 }}
                        />
                      </>
                    )}
                  </Box>
                }
                sx={{ pb: 1, flexWrap: 'wrap' }}
              />
              <CardContent sx={{ pt: 2 }}>
                {chartLoading ? (
                  <Box sx={{ p: 2 }}>
                    <Skeleton variant="rectangular" width="100%" height={340} sx={{ borderRadius: 2 }} />
                  </Box>
                ) : chartData && chartData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={340}>
                    <AreaChart data={chartData}>
                      <defs>
                        <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor={colors.primary.main} stopOpacity={0.3} />
                          <stop offset="95%" stopColor={colors.primary.main} stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke={colors.grey[200]} />
                      <XAxis
                        dataKey="date"
                        stroke={colors.grey[600]}
                        style={{ fontSize: '0.75rem' }}
                        tick={{ fill: colors.grey[600] }}
                      />
                      <YAxis
                        stroke={colors.grey[600]}
                        style={{ fontSize: '0.75rem' }}
                        tick={{ fill: colors.grey[600] }}
                        tickFormatter={(value) => {
                          if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(0)}M`;
                          if (value >= 1_000) return `${(value / 1_000).toFixed(0)}K`;
                          return value.toString();
                        }}
                      />
                      <Tooltip
                        formatter={formatChartTooltip}
                        labelStyle={{ fontWeight: 600, marginBottom: 8, color: colors.grey[900] }}
                        contentStyle={{
                          backgroundColor: 'white',
                          border: `1px solid ${colors.grey[200]}`,
                          borderRadius: '12px',
                          boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                          padding: '12px 16px',
                        }}
                        itemStyle={{
                          color: colors.grey[700],
                          fontWeight: 600,
                        }}
                      />
                      <Area
                        type="monotone"
                        dataKey="revenue"
                        stroke={colors.primary.main}
                        strokeWidth={3}
                        fillOpacity={1}
                        fill="url(#colorRevenue)"
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                ) : (
                  <Box
                    display="flex"
                    flexDirection="column"
                    justifyContent="center"
                    alignItems="center"
                    height={340}
                    sx={{
                      bgcolor: colors.grey[50],
                      borderRadius: 2,
                      border: `2px dashed ${colors.grey[300]}`,
                    }}
                  >
                    <Box
                      sx={{
                        bgcolor: 'white',
                        borderRadius: '50%',
                        p: 3,
                        mb: 2,
                        boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                      }}
                    >
                      <Assessment sx={{ fontSize: 48, color: colors.grey[400] }} />
                    </Box>
                    <Typography variant="subtitle1" color="text.primary" fontWeight={600} gutterBottom>
                      No Revenue Data Available
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 300, textAlign: 'center' }}>
                      Revenue trends will appear here once transactions are recorded
                    </Typography>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* County Performance */}
          <Grid size={{ xs: 12, lg: 4 }}>
            <Card
              elevation={0}
              sx={{
                borderRadius: 3,
                border: `1px solid ${colors.grey[200]}`,
                boxShadow: '0 4px 16px rgba(0, 0, 0, 0.08)',
                height: '100%',
                transition: 'all 0.3s ease',
                '&:hover': {
                  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
                  transform: 'translateY(-2px)',
                },
              }}
            >
              <CardHeader
                avatar={
                  <Box
                    sx={{
                      bgcolor: `${colors.success.main}15`,
                      borderRadius: 2,
                      p: 1,
                      display: 'flex'
                    }}
                  >
                    <TrendingUp sx={{ color: colors.success.main }} />
                  </Box>
                }
                title="Top Counties"
                subheader="By revenue performance"
                titleTypographyProps={{ variant: 'h6', fontWeight: 700 }}
                subheaderTypographyProps={{ variant: 'body2', color: 'text.secondary' }}
                sx={{ pb: 1 }}
              />
              <CardContent sx={{ pt: 2 }}>
                {performanceLoading ? (
                  <Box sx={{ p: 2 }}>
                    <Skeleton variant="rectangular" width="100%" height={340} sx={{ borderRadius: 2 }} />
                  </Box>
                ) : performance && performance.length > 0 ? (
                  <ResponsiveContainer width="100%" height={340}>
                    <BarChart data={performance.slice(0, 7)} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" stroke={colors.grey[200]} opacity={0.3} />
                      <XAxis
                        type="number"
                        stroke={colors.grey[600]}
                        style={{ fontSize: '0.75rem' }}
                        tick={{ fill: colors.grey[600] }}
                        tickFormatter={(value) => {
                          if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(0)}M`;
                          if (value >= 1_000) return `${(value / 1_000).toFixed(0)}K`;
                          return value.toString();
                        }}
                      />
                      <YAxis
                        dataKey="county_name"
                        type="category"
                        stroke={colors.grey[600]}
                        style={{ fontSize: '0.75rem' }}
                        width={100}
                        tick={{ fill: colors.grey[600] }}
                      />
                      <Tooltip
                        formatter={formatChartTooltip}
                        labelStyle={{ fontWeight: 600, marginBottom: 8, color: colors.grey[900] }}
                        contentStyle={{
                          backgroundColor: 'white',
                          border: `1px solid ${colors.grey[200]}`,
                          borderRadius: '12px',
                          boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                          padding: '12px 16px',
                        }}
                        itemStyle={{
                          color: colors.grey[700],
                          fontWeight: 600,
                        }}
                      />
                      <Bar
                        dataKey="revenue"
                        fill={colors.success.main}
                        radius={[0, 8, 8, 0]}
                        maxBarSize={35}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <Box
                    display="flex"
                    flexDirection="column"
                    justifyContent="center"
                    alignItems="center"
                    height={340}
                    sx={{
                      bgcolor: colors.grey[50],
                      borderRadius: 2,
                      border: `2px dashed ${colors.grey[300]}`,
                    }}
                  >
                    <Box
                      sx={{
                        bgcolor: 'white',
                        borderRadius: '50%',
                        p: 3,
                        mb: 2,
                        boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                      }}
                    >
                      <Business sx={{ fontSize: 48, color: colors.grey[400] }} />
                    </Box>
                    <Typography variant="subtitle1" color="text.primary" fontWeight={600} gutterBottom>
                      No County Data Available
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 260, textAlign: 'center' }}>
                      County performance rankings will be displayed here
                    </Typography>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Recent Transactions DataGrid */}
        <Card
          elevation={0}
          sx={{
            borderRadius: 3,
            overflow: 'hidden',
            border: `1px solid ${colors.grey[200]}`,
            boxShadow: '0 4px 16px rgba(0, 0, 0, 0.08)',
            transition: 'all 0.3s ease',
            '&:hover': {
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
            },
          }}
        >
          <CardContent sx={{ p: 0 }}>
            <Box sx={{ p: 3, borderBottom: `1px solid ${colors.grey[200]}`, bgcolor: colors.grey[50] }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Box
                    sx={{
                      bgcolor: `${colors.primary.main}15`,
                      borderRadius: 2,
                      p: 1,
                      display: 'flex'
                    }}
                  >
                    <Receipt sx={{ color: colors.primary.main }} />
                  </Box>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                      Recent Transactions
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Latest transactions across all counties
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                  <FormControl size="small" sx={{ minWidth: 120 }}>
                    <InputLabel>Show</InputLabel>
                    <Select
                      value={transactionLimit}
                      label="Show"
                      onChange={(e) => setTransactionLimit(Number(e.target.value))}
                    >
                      <MenuItem value={5}>5</MenuItem>
                      <MenuItem value={10}>10</MenuItem>
                      <MenuItem value={25}>25</MenuItem>
                      <MenuItem value={50}>50</MenuItem>
                      <MenuItem value={100}>100</MenuItem>
                    </Select>
                  </FormControl>
                  <Button
                    variant="contained"
                    size="small"
                    onClick={() => navigate('/superadmin/transactions')}
                    sx={{
                      background: `linear-gradient(135deg, ${colors.primary.main} 0%, ${colors.primary.dark} 100%)`,
                      '&:hover': {
                        background: `linear-gradient(135deg, ${colors.primary.dark} 0%, ${colors.primary.main} 100%)`,
                      },
                    }}
                  >
                    View All
                  </Button>
                </Box>
              </Box>
            </Box>
            {transactionsLoading ? (
              <Box display="flex" justifyContent="center" alignItems="center" py={8}>
                <CircularProgress />
              </Box>
            ) : (
              <Box sx={{ width: '100%' }}>
                <DataGrid
                  rows={transactions || []}
                  columns={columns}
                  autoHeight
                  pageSizeOptions={[10, 25, 50]}
                  initialState={{
                    pagination: { paginationModel: { pageSize: 10 } },
                  }}
                  disableRowSelectionOnClick
                  slots={{ toolbar: GridToolbar }}
                  slotProps={{
                    toolbar: {
                      showQuickFilter: true,
                      quickFilterProps: { debounceMs: 500 },
                    },
                  }}
                  sx={{
                    border: 'none',
                    '& .MuiDataGrid-toolbarContainer': {
                      padding: '16px 20px',
                      borderBottom: `1px solid ${colors.grey[200]}`,
                      backgroundColor: colors.grey[50],
                    },
                    '& .MuiDataGrid-cell': {
                      borderBottom: `1px solid ${colors.grey[100]}`,
                      fontSize: '0.875rem',
                      py: 1.5,
                    },
                    '& .MuiDataGrid-columnHeaders': {
                      backgroundColor: colors.grey[50],
                      borderBottom: `2px solid ${colors.grey[300]}`,
                      fontSize: '0.8125rem',
                      fontWeight: 600,
                      color: colors.grey[700],
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                    },
                    '& .MuiDataGrid-row': {
                      transition: 'background-color 0.15s ease',
                      '&:hover': {
                        backgroundColor: `${colors.primary.main}08`,
                        cursor: 'pointer',
                      },
                      '&:nth-of-type(even)': {
                        backgroundColor: 'rgba(0, 0, 0, 0.02)',
                      },
                    },
                    '& .MuiDataGrid-footerContainer': {
                      borderTop: `2px solid ${colors.grey[200]}`,
                      backgroundColor: colors.grey[50],
                      minHeight: '56px',
                    },
                  }}
                />
              </Box>
            )}
          </CardContent>
        </Card>
      </Box>
    </AdminLayout>
  );
};
